import { useEffect, useState } from "react";
import { Zap, Plus, Trash2, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

export function useQuickReplies() {
  const [items, setItems] = useState([]);
  const load = () => api.waQuickReplies().then((r) => setItems(r.items)).catch(() => {});
  useEffect(() => { load(); }, []);
  return { items, reload: load };
}

export function QuickReplyButtons({ items, onPick, handoffId }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`quick-replies-${handoffId}`}>
      {items.map((q) => (
        <button key={q.id} type="button" onClick={() => onPick(q.text)} title={q.text} data-testid={`quick-reply-${handoffId}-${q.id}`}
          className="inline-flex items-center gap-1 rounded-full border border-[var(--nc-blue)]/40 bg-[var(--nc-required)] text-[var(--nc-blue)] px-2.5 py-1 text-[11px] font-semibold hover:bg-[var(--nc-blue)] hover:text-white transition-colors">
          <Zap className="h-3 w-3" /> {q.title}
        </button>
      ))}
    </div>
  );
}

export function QuickRepliesManager({ items, onChanged }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!title.trim() || !text.trim()) return toast.error("Inserisci titolo e testo");
    setBusy(true);
    try { await api.waQuickReplyCreate({ title: title.trim(), text: text.trim() }); setTitle(""); setText(""); toast.success("Risposta rapida aggiunta"); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.waQuickReplyDelete(id); toast.success("Risposta eliminata"); onChanged(); } catch { toast.error("Eliminazione non riuscita"); }
  };
  return (
    <>
      <Button size="sm" variant="ghost" className="h-8 text-xs gap-1.5" onClick={() => setOpen(true)} data-testid="quick-replies-manage"><Settings2 className="h-3.5 w-3.5" /> Risposte rapide</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="quick-replies-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5" /> Risposte rapide operatore</DialogTitle>
            <DialogDescription>Testi pronti per rispondere con un clic ai clienti in attesa. Il testo va nella casella e puoi modificarlo prima di inviare.</DialogDescription>
          </DialogHeader>
          <ul className="space-y-2 max-h-64 overflow-auto" data-testid="quick-replies-list">
            {items.map((q) => (
              <li key={q.id} className="flex items-start gap-2 rounded-lg border border-border p-2.5" data-testid={`quick-reply-row-${q.id}`}>
                <div className="flex-1 min-w-0"><p className="text-sm font-semibold">{q.title}</p><p className="text-xs text-muted-foreground line-clamp-2">{q.text}</p></div>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => remove(q.id)} data-testid={`quick-reply-delete-${q.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </li>
            ))}
            {items.length === 0 && <li className="text-sm text-muted-foreground">Nessuna risposta rapida: aggiungine una qui sotto.</li>}
          </ul>
          <div className="space-y-2 border-t border-border pt-3">
            <div className="space-y-1"><Label className="text-xs">Titolo</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60} placeholder="Es. Presa in carico" className="h-9" data-testid="quick-reply-title-input" required /></div>
            <div className="space-y-1"><Label className="text-xs">Testo</Label><Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Testo che verrà inviato al cliente…" data-testid="quick-reply-text-input" required /></div>
            <Button size="sm" className="gap-1.5" disabled={busy} onClick={add} data-testid="quick-reply-add"><Plus className="h-3.5 w-3.5" /> Aggiungi</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
